import { useState } from 'react';

interface ContactFormProps {
  title: string;
  subtitle: string;
  placeholder: string;
  buttonText: string;
  isVisible: boolean;
}

export default function ContactForm({
  title,
  subtitle,
  placeholder,
  buttonText,
  isVisible,
}: ContactFormProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setStatus('success');
    setEmail('');

    // Reset after 3 seconds
    setTimeout(() => setStatus('idle'), 3000);
  };

  return (
    <section className="py-32 px-6 relative overflow-hidden">
      {/* Animated gradient background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, transparent 50%, rgba(255, 149, 0, 0.1) 100%)',
          animation: 'gradient-shift 10s ease infinite',
          backgroundSize: '200% 200%',
        }}
      />

      <div
        className={`max-w-4xl mx-auto text-center relative z-10 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <h2 className="text-4xl md:text-6xl font-bold mb-6">{title}</h2>
        <p className="text-xl md:text-2xl text-gray-300 mb-12">{subtitle}</p>

        {/* Contact form */}
        <form onSubmit={handleSubmit} className="max-w-md mx-auto">
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={placeholder}
              disabled={status === 'loading' || status === 'success'}
              className="flex-1 px-6 py-4 rounded-xl bg-white/10 border border-white/20 focus:border-[var(--cyan-electric)] focus:outline-none focus:ring-2 focus:ring-[var(--cyan-electric)]/20 transition-all placeholder-gray-500 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={status === 'loading' || status === 'success' || !email}
              className="rgb-border-hover px-8 py-4 rounded-xl bg-gradient-to-r from-[var(--cyan-electric)] to-cyan-400 text-[var(--navy-deep)] font-semibold transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100 disabled:animation-none"
            >
              {status === 'loading' ? (
                <span className="inline-block w-5 h-5 border-2 border-[var(--navy-deep)] border-t-transparent rounded-full animate-spin" />
              ) : status === 'success' ? (
                '✓'
              ) : (
                buttonText
              )}
            </button>
          </div>

          {status === 'success' && (
            <p className="mt-4 text-[var(--cyan-electric)] animate-fade-in">
              Thank you! We'll be in touch soon.
            </p>
          )}
        </form>
      </div>
    </section>
  );
}
